import { Outlet, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppStore } from '../stores/app-store';
import { colorThemes } from '../lib/themes/theme-config';
import { LandingNavbar } from '../components/layout/landing-navbar';
import { MobileAppBanner } from '../components/layout/mobile-app-banner';
import { applyTheme, watchSystemTheme } from '../lib/themes/apply-theme';
import { useLanguageSync } from '../hooks/use-language-sync';
import { usePageTracking } from '../hooks/use-page-tracking';
import { usePendingInvite } from '../features/contacts/join-page';

const AppHeader = lazy(() =>
  import('../components/layout/app-header').then((module) => ({
    default: module.AppHeader,
  })),
);

const MobileNav = lazy(() =>
  import('../components/layout/mobile-nav').then((module) => ({
    default: module.MobileNav,
  })),
);

/**
 * Obituary print + iframe embed URLs must never show AppHeader / LandingNavbar /
 * MobileNav. Top-level routes in router.tsx bypass AppShell entirely; this matcher
 * is a safety net if those routes are ever nested under AppShell again.
 * Only `/memorial/:slug/obituary/embed` and `/print` — not `/obituary`, `/edit`, etc.
 */
function isObituaryShellFreePath(pathname: string): boolean {
  return /^\/memorial\/[^/]+\/obituary\/(embed|print)\/?$/.test(pathname);
}

/**
 * Global Stripe-cancel feedback. Checkout returns to the exact page it was opened
 * from with ?checkout=cancelled; we surface one quiet toast and strip the flag,
 * on any route — so no individual page needs its own cancel handler.
 */
function useCheckoutCancelToast() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('checkout') !== 'cancelled') return;
    toast(t('checkout.cancelled'), {
      id: 'checkout-cancelled',
    });
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);
}

export function AppShell() {
  const { i18n } = useTranslation();
  const { colorTheme, mode, language, setColorTheme } = useAppStore();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Self-heal stale persisted local storage themes gracefully
  useEffect(() => {
    if (!colorThemes[colorTheme]) {
      setColorTheme('ocean');
    }
  }, [colorTheme, setColorTheme]);

  // Process pending invite codes after auth
  usePendingInvite();

  // Sync language between i18next and app store
  useLanguageSync();

  // Track page views on every route change
  usePageTracking();

  // Surface Stripe checkout-cancel feedback anywhere in the app
  useCheckoutCancelToast();

  // Landing pages (show landing navbar)
  const landingPages = ['/', '/about', '/features', '/contact', '/partners', '/support', '/honored'];
  const isLandingPage = landingPages.includes(location.pathname);

  // Auth pages (show landing navbar)
  const authPages = ['/sign-in', '/sign-up', '/invite', '/verify-otp', '/verify-email', '/partner/verify-email', '/forgot-password', '/reset-password'];
  const isAuthPage = authPages.includes(location.pathname);

  // Memorial pages — immersive (hide chrome on mobile)
  const isMemorialPage = location.pathname.startsWith('/memorial/');

  const isChromeFreeObituaryPage = isObituaryShellFreePath(location.pathname);

  // Light a candle — immersive (hide top header on mobile, keep bottom nav)
  const isLightACandlePage = location.pathname === '/light-a-candle';
  const isImmersivePage = isMemorialPage || isLightACandlePage;

  // App pages (show app header + mobile nav). Chrome-free obituary paths never get layout.
  const showAppLayout = !isLandingPage && !isAuthPage && !isChromeFreeObituaryPage;
  const showLandingNavbar = (isLandingPage || isAuthPage) && !isChromeFreeObituaryPage;

  // Keep a ref to always have the latest values inside the system-watcher closure
  const colorThemeRef = useRef(colorTheme);
  const modeRef = useRef(mode);
  useLayoutEffect(() => {
    colorThemeRef.current = colorTheme;
    modeRef.current = mode;
  });

  // Apply theme synchronously before paint to prevent flash of wrong theme
  useLayoutEffect(() => {
    applyTheme(colorTheme, mode);

    // Only attach the system watcher when mode is 'system'
    if (mode === 'system') {
      const cleanup = watchSystemTheme(() => {
        // Use refs so we always read the latest colorTheme and mode
        applyTheme(colorThemeRef.current, modeRef.current);
      });
      return cleanup;
    }
  }, [colorTheme, mode]);

  useEffect(() => {
    // Ensure i18next language matches app store
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }

    // Set language attribute for translations, but keep direction LTR for app UI
    // Individual pages (like memorial pages) can override direction as needed
    const root = window.document.documentElement;
    root.setAttribute('dir', 'ltr');
    root.setAttribute('lang', language);
  }, [language, i18n]);

  // Scroll to top on forward navigation only.
  // On POP (browser back/forward) skip so the user lands where they left off.
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'POP' && mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  // Pages with sticky sub-headers need zero top padding on main
  // so their sticky elements sit flush against the AppHeader
  const isFlushTopRoute = location.pathname === '/home'
    || location.pathname === '/channels'
    || location.pathname.startsWith('/channels/')
    || location.pathname.startsWith('/atag/')
    || /^\/memorial\/[^/]+\/settings$/.test(location.pathname)
    || location.pathname === '/partner/dashboard'
    || location.pathname === '/partner/panel';

  return (
    <div
      className={
        isChromeFreeObituaryPage
          ? 'min-h-screen flex flex-col bg-background text-foreground'
          : 'h-screen flex flex-col overflow-hidden bg-background text-foreground'
      }
    >
      {!isChromeFreeObituaryPage && <MobileAppBanner />}
      {showLandingNavbar && <LandingNavbar />}
      {/* Hide top header on immersive pages for mobile; always show on desktop */}
      {showAppLayout && (
        <Suspense fallback={null}>
          <div className={isImmersivePage ? 'hidden md:block' : ''}>
            <AppHeader />
          </div>
        </Suspense>
      )}
      <main
        ref={mainRef}
        className={
          isChromeFreeObituaryPage
            ? 'flex-1 min-h-0 overflow-y-auto p-0'
            : `flex-1 overflow-y-auto ${showAppLayout ? (isFlushTopRoute ? 'pb-16 md:pb-0' : isImmersivePage ? (isLightACandlePage ? 'pb-16 md:pb-0' : 'pb-0 md:pt-6 md:pb-0') : 'pt-6 pb-16 md:pb-0') : ''}`
        }
      >
        <Outlet />
      </main>
      {/* Show bottom nav on all app pages except memorial pages */}
      {showAppLayout && !isMemorialPage && (
        <Suspense fallback={null}>
          <MobileNav />
        </Suspense>
      )}
    </div>
  );
}
